import { useState } from "react";
import { Link } from "react-router";
import { useAuth } from "react-oidc-context";
import { Menu } from "lucide-react";

import { ErrorBox } from "@/components/Spinner";
import { Button, ButtonLink, IconButton } from "@/components/ui";
import { useRequestLeave } from "@/hooks/useUnsavedChanges";
import { useShellState } from "@/hooks/useShellState";
import { setLoginHint } from "@/auth/loginHint";

export function TopBar() {
  const auth = useAuth();
  const { toggleSidebar } = useShellState();
  // Signing out loses what a form holds as surely as leaving its page does: same question, asked once.
  const requestLeave = useRequestLeave();
  // A sign-out that did not happen (the request to the identity service failed) is said, under the bar,
  // where the operator is looking: the page and what they were typing are still there.
  const [signOutError, setSignOutError] = useState<unknown>(null);

  return (
    <>
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-line bg-surface px-3">
        <div className="flex items-center gap-2">
          <IconButton icon={Menu} label="Show or hide sidebar" onClick={toggleSidebar} />
          <Link to="/" className="flex items-center gap-2 font-semibold text-ink">
            <img
              src="/hapsout-mark-dark.png"
              alt="Hapsout"
              className="h-7 w-7"
              width={28}
              height={28}
            />
            PulseStation · <span className="text-brand-600">Control Plane</span>
          </Link>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-ink-muted">
            {auth.user?.profile.email ?? auth.user?.profile.sub}
          </span>
          <ButtonLink to="/security" variant="ghost">
            Security
          </ButtonLink>
          <Button
            variant="ghost"
            onClick={() => {
              setSignOutError(null);
              requestLeave(() => {
                // Se déconnecter, c'est oublier la méthode : l'écran suivant propose
                // les deux, au lieu de renvoyer seul vers le Hosted UI.
                setLoginHint(null);
                // The promise goes back to the guard: a failure gives the browser's own question back
                // (a form may still hold what was typed) and is said here, never swallowed.
                return auth.signoutRedirect().catch((error: unknown) => {
                  setSignOutError(error);
                  throw error;
                });
              });
            }}
          >
            Sign out
          </Button>
        </div>
      </header>
      {signOutError !== null && (
        <div className="shrink-0 border-b border-line bg-surface px-3 py-2">
          <ErrorBox error={signOutError} title="Could not sign out" onDismiss={() => setSignOutError(null)} />
        </div>
      )}
    </>
  );
}
